import React, { useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";
import autoTable from "jspdf-autotable";
import { PDFGeneratorContext } from "./PDFGeneratorContext";
import { useAppTheme } from "../../utils/theme";

const PDF_WIDTH = 842; // A4 landscape in pt
const PDF_HEIGHT = 595;

/**
 * SIMS-8360 (FR-14) — fit a captured certificate image onto the A4 page WITHOUT distorting it.
 *
 * The on-screen certificate is ~16:9; A4 landscape is ~1.41:1. Stretching the image to fill the page
 * (the old behaviour) distorts it, so the download/email no longer matches the preview. Instead we
 * scale the image to fit inside the page preserving its aspect ratio, and CENTER it (letterbox) — so
 * the PDF reproduces the preview's proportions exactly. Pure function, unit-tested.
 */
export function fitCentered(
	imgWidth: number,
	imgHeight: number,
	pageWidth: number = PDF_WIDTH,
	pageHeight: number = PDF_HEIGHT,
): { drawWidth: number; drawHeight: number; offsetX: number; offsetY: number } {
	// Guard against a zero/invalid image so a bad capture can't produce NaN geometry.
	if (!imgWidth || !imgHeight || imgWidth <= 0 || imgHeight <= 0) {
		return {
			drawWidth: pageWidth,
			drawHeight: pageHeight,
			offsetX: 0,
			offsetY: 0,
		};
	}
	const imgRatio = imgWidth / imgHeight;
	const pageRatio = pageWidth / pageHeight;
	let drawWidth = pageWidth;
	let drawHeight = pageHeight;
	if (imgRatio > pageRatio) {
		// wider than the page → fit to width, letterbox top/bottom
		drawHeight = pageWidth / imgRatio;
	} else {
		// taller than the page → fit to height, letterbox left/right
		drawWidth = pageHeight * imgRatio;
	}
	const offsetX = (pageWidth - drawWidth) / 2;
	const offsetY = (pageHeight - drawHeight) / 2;
	return { drawWidth, drawHeight, offsetX, offsetY };
}

const PDFGeneratorProvider = ({ children }: { children: React.ReactNode }) => {
	const theme = useAppTheme();
	const downloadRef = useRef(null);

	// Captures the downloadRef as a canvas screenshot, then places it in a PDF
	const captureAsPDF = async (): Promise<jsPDF | null> => {
		const input = downloadRef.current as HTMLElement | null;
		if (!input) return null;

		// scale: 2 is enough to render crisply on-screen and in print without the
		// file bloat of scale: 3 (which produced ~16MB PNGs).
		//
		// SIMS-8351: pin the clone's LAYOUT VIEWPORT to the element's own box. html2canvas clones into an
		// iframe whose layout viewport defaults to `window.innerWidth`, so the clone re-flowed at the
		// browser window width rather than at the certificate's width. Anything sized from a measured
		// element width — the certificate's type scale and gaps both are — then rendered at live-DOM
		// pixel values inside a differently-sized box, which is why the PDF came out visibly more
		// cramped than the preview even after the viewport-unit fix. FR-14 promises the PDF matches the
		// preview, so the clone has to lay out at the same width.
		//
		// windowWidth/windowHeight ONLY — deliberately NOT `width`/`height`. Passing those made
		// html2canvas allocate an output canvas of the rounded-up size while the cloned element kept its
		// fractional box, leaving a blank sub-pixel strip along the right and bottom edges (visible as
		// padding outside the frame).
		const rect = input.getBoundingClientRect();
		const canvas = await html2canvas(input, {
			scale: 2,
			useCORS: true,
			allowTaint: true,
			backgroundColor: theme.palette.surface.light,
			windowWidth: Math.round(rect.width),
			windowHeight: Math.round(rect.height),
		});

		// JPEG at high quality keeps the file small (a full-bleed PNG was ~16MB;
		// this is well under 1MB) while staying visibly sharp.
		const imgData = canvas.toDataURL("image/jpeg", 0.92);

		// Standard A4 landscape page, with the certificate CENTERED and scaled to fit (letterboxed) so it
		// keeps its true proportions instead of being stretched to the A4 shape (FR-14 fidelity).
		const pdf = new jsPDF("landscape", "pt", "a4");
		const { drawWidth, drawHeight, offsetX, offsetY } = fitCentered(
			canvas.width,
			canvas.height,
		);
		pdf.addImage(imgData, "JPEG", offsetX, offsetY, drawWidth, drawHeight);
		return pdf;
	};

	const handleBase64Certificate = (callback: (base64: string) => void) => {
		captureAsPDF().then((pdf) => {
			if (pdf) {
				const pdfBase64 = pdf.output("datauristring");
				callback(pdfBase64);
			}
		});
	};

	const handleDownloadCertificate = (fileName: string) => {
		captureAsPDF().then((pdf) => {
			if (pdf) {
				pdf.save(fileName);
			}
		});
	};

	const generateTablePDF = (
		headers: string[],
		// TODO: use same type when defined above
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		data: any[],
		fileName: string,
	) => {
		const doc = new jsPDF("landscape", "pt", "a4");

		autoTable(doc, {
			head: [headers],
			body: data,
		});

		doc.save(fileName);
	};
	return (
		<PDFGeneratorContext.Provider
			value={{
				downloadRef,
				handleDownloadCertificate,
				handleBase64Certificate,
				generateTablePDF,
			}}
		>
			{children}
		</PDFGeneratorContext.Provider>
	);
};

export default PDFGeneratorProvider;
